// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL RELOJ EN LA RAÍZ DE LA APP                                                               (t271, 16-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Lo monta App.jsx por fuera de las rutas, así que nada de lo que hay aquí se desmonta al cambiar de página: la ventana
//  del reloj (components/reloj/VentanaReloj.jsx) se abre encima de cualquier pantalla y el aviso del final
//  (components/reloj/AvisoTiempoCumplido.jsx) sale donde se esté. Lo que cuenta vive en utils/reloj.js (`elReloj`), uno
//  por pestaña del navegador; esto solo lo escucha y lo enseña, y lo da a quien lo pida con `useReloj()`.
//
//  AL TERMINAR LA CUENTA ATRÁS, una vez por aviso (el aviso lo crea utils/reloj.js · comprobar, uno por cuenta):
//   · el sonido, si la casilla «Sonido al terminar» está puesta (utils/relojAviso · sonarAviso), hasta «Parar»;
//   · con la pestaña escondida, el título parpadea «Tiempo cumplido» hasta volver a ella y, si el navegador ya tiene
//     permiso, sale una notificación (aquí no se pide el permiso);
//   · el parpadeo de la pantalla y el aviso con «Parar», en AvisoTiempoCumplido.
//  El sonido y la notificación se lanzan al CAMBIAR el estado (un aviso nuevo), no al montarse un efecto: React, en
//  desarrollo, monta cada efecto dos veces y sonaría dos veces o ninguna. `tomarAviso` lo asegura además.
//
//  Y AL VOLVER A LA PESTAÑA (o a la ventana), se mira si ya es la hora: el navegador frena los temporizadores de las
//  pestañas escondidas, y así el final nunca llega tarde a lo que se ve.
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { elReloj, duracionEnPalabras } from '../utils/reloj'
import { sonarAviso, notificar, parpadearTitulo, prepararSonido, TXT_TIEMPO_CUMPLIDO } from '../utils/relojAviso'
import VentanaReloj from '../components/reloj/VentanaReloj'
import AvisoTiempoCumplido from '../components/reloj/AvisoTiempoCumplido'

const RelojContext = createContext(null)

export function useReloj() {
  const v = useContext(RelojContext)
  if (v) return v
  const reloj = elReloj()
  return { reloj, estado: reloj.estado() }
}

const escondida = () => { try { return typeof document !== 'undefined' && !!document.hidden } catch (_) { return false } }

export function RelojProvider({ children, reloj: dado }) {
  const reloj = dado || elReloj()
  const [estado, setEstado] = useState(() => reloj.estado())

  // Escuchar el reloj, lanzar el sonido y la notificación de cada aviso nuevo y apagarlos con «Parar».
  useEffect(() => {
    let anterior = reloj.estado().aviso
    let callar = null
    const alCambiar = (e) => {
      setEstado(e)
      const a = e.aviso
      if (a && (!anterior || anterior.id !== a.id) && reloj.tomarAviso('efectos:' + a.id)) {
        if (e.sonido) { if (callar) callar(); callar = sonarAviso(typeof window !== 'undefined' ? window : null) }
        if (escondida()) {
          notificar(typeof window !== 'undefined' ? window : null, {
            titulo: TXT_TIEMPO_CUMPLIDO,
            cuerpo: a.total ? 'La cuenta atrás de ' + duracionEnPalabras(a.total) + ' ha terminado.' : 'La cuenta atrás ha terminado.',
          })
        }
      }
      // «Parar», o quitar la casilla del sonido mientras suena: se calla.
      if (callar && (!a || !e.sonido)) { callar(); callar = null }
      anterior = a
    }
    const quitar = reloj.suscribir(alCambiar)
    setEstado(reloj.estado())
    // Lo recuperado al recargar que ya había terminado avisa ahora, con todo esto ya escuchando.
    reloj.comprobar()
    return () => { quitar(); if (callar) { callar(); callar = null } }
  }, [reloj])

  // Volver a la pestaña o a la ventana: ¿ya es la hora?
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined
    const mirar = () => reloj.comprobar()
    document.addEventListener('visibilitychange', mirar)
    window.addEventListener('focus', mirar)
    return () => { document.removeEventListener('visibilitychange', mirar); window.removeEventListener('focus', mirar) }
  }, [reloj])

  // EL ALTAVOZ, CON CUALQUIER TOQUE. Al recargar la página con una cuenta en marcha nadie ha pulsado «Empezar» en esta
  // carga, y el navegador no deja sonar nada hasta el primer toque: con él (en cualquier sitio) se prepara.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const preparar = () => { const c = reloj.estado().cuenta; if (c.estado === 'marcha' || c.estado === 'pausa') prepararSonido() }
    document.addEventListener('pointerdown', preparar, true)
    document.addEventListener('keydown', preparar, true)
    return () => { document.removeEventListener('pointerdown', preparar, true); document.removeEventListener('keydown', preparar, true) }
  }, [reloj])

  // El título de la pestaña parpadea mientras el aviso está puesto y la pestaña escondida.
  const idAviso = estado.aviso ? estado.aviso.id : null
  useEffect(() => {
    if (idAviso == null || typeof document === 'undefined') return undefined
    let parar = null
    const ajustar = () => {
      if (escondida()) { if (!parar) parar = parpadearTitulo(document) }
      else if (parar) { parar(); parar = null }
    }
    ajustar()
    document.addEventListener('visibilitychange', ajustar)
    return () => { document.removeEventListener('visibilitychange', ajustar); if (parar) parar() }
  }, [idAviso])

  const parar = useCallback(() => reloj.pararAviso(), [reloj])
  const masMinuto = useCallback(() => { prepararSonido(); reloj.masUnMinuto() }, [reloj])

  return (
    <RelojContext.Provider value={{ reloj, estado }}>
      {children}
      {estado.ventana && <VentanaReloj reloj={reloj} estado={estado} />}
      {estado.aviso && <AvisoTiempoCumplido key={estado.aviso.id} aviso={estado.aviso} onParar={parar} onMasMinuto={masMinuto} />}
    </RelojContext.Provider>
  )
}
